import { CarModelMainDto } from "api"
import { useIsDesktop } from "common/MediaQueries"
import TruncateText from "common/bit/TruncateText"
import moment from "moment"
import Image from "next/image"
import { useInView } from "react-intersection-observer"
import {
  StyledBody,
  StyledHeaderH2,
  StyledLink,
} from "reusable/wrappers/BodyText"
import styled from "styled-components"
import { Navigation, Pagination } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import { StyledSvgIcon } from "theme/shared"

export const StyledPageWrapper = styled.div<{
  $padding?: string
  $paddingTop?: string
  $background?: string
  $inView: boolean
}>`
  display: flex;
  justify-content: center;
  padding-top: 56px;
  padding-bottom: ${(p) => (p.$padding ? `${p.$padding}` : "60px")};
  ${(p) => p.theme.breakpoints.up("md")} {
    padding-top: ${(p) => (p.$paddingTop ? p.$paddingTop : "112px")};
    padding-bottom: ${(p) => (p.$padding ? `${p.$padding}` : "100px")};
  }
  background: ${(p) => p.$background && p.$background};
  transform: ${({ $inView }) =>
    !$inView ? "translateY(20px)" : "translateX(0)"};
  opacity: ${({ $inView }) => ($inView ? "1" : "0")};

  transition: all 0.9s 0.9s cubic-bezier(0.19, 0.88, 0.53, 1.12);
  transition-delay: 0.3s;
`

const StyledImage = styled(Image)`
  transition: 1s;
  :hover,
  :focus {
    transform: scale(1.2);
  }
`
const StyledSwiper = styled(Swiper)`
  width: 100%;
  height: 100%;
  margin-top: 30px;

  .swiper-slide {
    width: max-content;
    height: auto;
    ${(p) => p.theme.breakpoints.down("sm")} {
      &:first-child {
        padding-right: 16px;
      }
    }

    ${(p) => p.theme.breakpoints.up("md")} {
      width: 280px !important;
    }
  }
`

const StyledSliderWrapper = styled.div`
  background: ${(p) => p.theme.colors.white};
  height: 400px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  margin-bottom: 10px;
  position: relative;
  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 100%;
  }
`

const StyledSwiperWrapper = styled.div`
  padding: 40px 0px;
  text-align: center;
  width: 100%;
  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 80px 20px;
  }
  h3 {
    width: 220px;
  }
`

const StyledSubtitleWrapper = styled.div`
  display: flex;
  justify-content: space-between !important;
  padding-left: 20px;
  padding-right: 20px;
  p {
    font-size: 16px;
    color: ${(p) => p.theme.colors.onyx};
  }
`

const StyledBottomText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    h3 {
      width: 230px;
      letter-spacing: 0px;
    }
  }
`
const StyledIconWrapper = styled.div`
  width: 24px;
  height: 24px;
`
const StyledLinkWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 0px;
  position: absolute;
  bottom: 10px;
`
const StyledImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const StyledLogoWrapper = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
`
const StyledArticlePage = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledTruncateText = styled(TruncateText)`
  text-align: center;
  color: ${(p) => p.theme.colors.disable};
  font-size: 20px;
  font-weight: 300;
  line-height: 130%;
  color: ${(p) => p.theme.colors.black};
  letter-spacing: 0.03em;
`

type ReviewsSectionType = {
  carData: CarModelMainDto
}

const ReviewsSection = ({ carData }: ReviewsSectionType) => {
  const { ref, inView } = useInView({ triggerOnce: true })
  const isDesktop = useIsDesktop()

  return (
    <StyledPageWrapper $inView={inView} ref={ref} $padding="0" $paddingTop="0">
      <StyledArticlePage>
        <StyledSwiperWrapper>
          <StyledHeaderH2>סיקור הדגם בתקשורת</StyledHeaderH2>
          <StyledSwiper
            slidesPerView={isDesktop ? "auto" : 1.2}
            spaceBetween={16}
            slidesPerGroup={1}
            loop={false}
            modules={[Pagination, Navigation]}
            centerInsufficientSlides
            centeredSlidesBounds
            centeredSlides={false}
            slidesOffsetAfter={!isDesktop ? 16 : 0}
          >
            {carData?.articles?.map((item, index) => (
              <SwiperSlide key={`review-slide-${index}`}>
                <StyledSliderWrapper>
                  <StyledImageWrapper>
                    <StyledImage
                      width={300}
                      height={220}
                      src={item?.image?.imageUrl}
                      alt={`כתבה מעיתון ${item?.name}`}
                      objectFit="cover"
                    />
                    <StyledLogoWrapper>
                      <Image
                        width={70}
                        height={27}
                        src={item?.originImage?.imageUrl}
                        alt="img"
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          objectFit: "cover",
                        }}
                      />
                    </StyledLogoWrapper>
                  </StyledImageWrapper>
                  <StyledSubtitleWrapper>
                    <StyledBody>{item?.auther}</StyledBody>
                    <StyledBody>
                      {moment(item?.datePublished).format("d.M.yyyy")}
                    </StyledBody>
                  </StyledSubtitleWrapper>
                  <StyledBottomText>
                    <StyledTruncateText
                      line={2}
                      element="span"
                      truncateText="…"
                      text={item.name}
                    />
                    <StyledLinkWrapper>
                      <StyledIconWrapper />
                      <StyledLink
                        $color="#101010"
                        href={item?.linkUrl}
                        target="_blank"
                      >
                        לכתבה המלאה
                      </StyledLink>
                      <StyledIconWrapper>
                        <StyledSvgIcon
                          src="/icons/exit-site.svg"
                          alt="מעבר לאתר חיצוני"
                          $w={24}
                          $h={24}
                        />
                      </StyledIconWrapper>
                    </StyledLinkWrapper>
                  </StyledBottomText>
                </StyledSliderWrapper>
              </SwiperSlide>
            ))}
          </StyledSwiper>
        </StyledSwiperWrapper>
        {/* <RegulationPage data={carData?.regulation} catalog={carData?.catalogs} /> */}
      </StyledArticlePage>
    </StyledPageWrapper>
  )
}
export default ReviewsSection
